import { PropsWithChildren, useContext, useMemo, createContext } from 'react';
import { RouteLocationType } from '@/api/apis/route';
import { RouteContextResourcesType } from '@/types/route';
import { CaseResourceType } from '@/api/apis/case';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { WithExtendedLocation } from '@/utils/helpers';

export type ExtendedRouteLocation = WithExtendedLocation<RouteLocationType>;

type RouteProviderProps = {
  locations: RouteLocationType[];
  resources: RouteContextResourcesType;
};

type RouteResourceIndex = {
  [K in keyof RouteContextResourcesType]: ReadonlyMap<string, RouteContextResourcesType[K][number]>;
};

type RouteContextType = RouteProviderProps & {
  locationById: ReadonlyMap<string, RouteLocationType>;
  extendedLocationById: ReadonlyMap<string, ExtendedRouteLocation>;
  locationsExtended: ExtendedRouteLocation[];
  resourceById: RouteResourceIndex;
};

type ExtendLocation = (location: RouteLocationType) => ExtendedRouteLocation;

export const buildRouteIndexes = (
  locations: RouteLocationType[],
  resources: RouteContextResourcesType,
  extendLocation: ExtendLocation,
): RouteContextType => {
  const locationById = new Map<string, RouteLocationType>();
  const extendedLocationById = new Map<string, ExtendedRouteLocation>();
  const locationsExtended: ExtendedRouteLocation[] = [];

  for (const location of locations) {
    const extended = extendLocation(location);
    locationsExtended.push(extended);

    if (!locationById.has(location.id)) {
      locationById.set(location.id, location);
      extendedLocationById.set(location.id, extended);
    }
  }

  const resourceById = Object.fromEntries(
    Object.entries(resources).map(([type, list]) => {
      const map = new Map<string, RouteContextResourcesType[keyof RouteContextResourcesType][number]>();
      for (const item of list) {
        if (!map.has(item.resourceId)) map.set(item.resourceId, item);
      }
      return [type, map];
    }),
  ) as unknown as RouteResourceIndex;

  return { locations, resources, locationById, extendedLocationById, locationsExtended, resourceById };
};

const IDENTITY_EXTEND_LOCATION: ExtendLocation = (location) => location as ExtendedRouteLocation;

const DEFAULT_ROUTE_CONTEXT: RouteContextType = buildRouteIndexes(
  [],
  {
    [CaseResourceType.ClinicalSpecialist]: [],
    [CaseResourceType.OCSConsole]: [],
    [CaseResourceType.Surgeon]: [],
    [CaseResourceType.Aircraft]: [],
    [CaseResourceType.Crew]: [],
  },
  IDENTITY_EXTEND_LOCATION,
);

export const RouteContext = createContext<RouteContextType>(DEFAULT_ROUTE_CONTEXT);

export const RouteProvider = ({ resources, locations, children }: PropsWithChildren<RouteProviderProps>) => {
  const { extendLocation } = useCountriesData();

  const value = useMemo(
    () => buildRouteIndexes(locations, resources, extendLocation),
    [locations, resources, extendLocation],
  );

  return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
};

export const useRouteContext = () => {
  const context = useContext(RouteContext);
  if (!context) {
    throw new Error('useRouteContext must be used within an RouteContext');
  }
  return context;
};
